import { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, generatePath } from 'react-router-dom';

import Button from 'components/Button';
import Container from 'components/Container';
import CertificateProgress from 'pages/Course/CertificateProgress';
import Details from 'pages/Course/Details';
import Icon from 'components/Icon';
import Modal from 'components/Modal';

import UnsupportedFile from 'components/UnsupportedFile';

import { COURSE_IDS, COURSE_TYPES, ROUTES } from 'constants/index';
import { AppContext } from 'services/context';
import { useScreenSize, useToggle, useTranslation } from 'services/hooks';

import { COURSES_ACTIONS } from 'services/reducers/actionTypes';

import './style.css';

const Course = () => {
	const { courses, dispatchCourses, Api } = useContext(AppContext);

	const [currentCourse, setCurrentCourse] = useState(undefined);
	const [certificatePdf, setCertificatePdf] = useState(undefined);

	const [pdfToDisplay, setPdfToDisplay] = useState(undefined);
	const [pdfModalVisible, togglePdfModalVisible] = useToggle(false);

	const [pdfBlob, setPdfBlob] = useState(undefined);
	const [eslModalVisible, toggleESLModalVisible] = useToggle(false);
	const [ieltsModalVisible, toggleIeltsModalVisible] = useToggle(false);

	const screenSize = useScreenSize();
	const { recordId } = useParams();
	const navigate = useNavigate();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'course';

	const TABLET_SIZE = process.env.REACT_APP_TABLET_SIZE;

	const passedAllTests = useMemo(() => {
		const chaptersWithoutFinal = currentCourse?.courseChapters?.filter(chapter => !chapter.isFinal);
		const passed = chaptersWithoutFinal?.every(chapter => chapter.courseChapterStatus >= 2 || !chapter.testUrl);
		return Boolean(passed);
	}, [currentCourse]);

	const courseCertificateEligible = useMemo(() => {
		return (
			currentCourse?.courseTypeId === COURSE_TYPES.INTERNAL ||
			currentCourse?.courseTypeId === COURSE_TYPES.IELTS ||
			currentCourse?.courseTypeId === COURSE_TYPES.IELTS_GENERAL ||
			currentCourse?.courseTypeId === COURSE_TYPES.TEF ||
			currentCourse?.courseTypeId === COURSE_TYPES.TOFEL
		);
	}, [currentCourse]);

	//to show the certificate component in IELTS General course only if he passed the test
	const displayCourseCertificateGeneralIELTS = useMemo(() => {
		return (
			(currentCourse?.courseTypeId === COURSE_TYPES.IELTS || currentCourse?.courseTypeId === COURSE_TYPES.IELTS_GENERAL) &&
			currentCourse?.rateOfProgress >= 100
		);
	}, [currentCourse]);

	const isIELTSCourse = useMemo(() => {
		return currentCourse?.courseTypeId === COURSE_TYPES.IELTS || currentCourse?.courseTypeId === COURSE_TYPES.IELTS_GENERAL;
	}, [currentCourse]);

	const isTEFCourse = useMemo(() => {
		return currentCourse?.courseTypeId === COURSE_TYPES.TEF;
	}, [currentCourse]);

	const isTOFELCourse = useMemo(() => {
		return currentCourse?.courseTypeId === COURSE_TYPES.TOFEL;
	}, [currentCourse]);

	const isESL_PremiumCourse = useMemo(() => {
		return currentCourse?.courseTypeId === COURSE_TYPES.ESL;
	}, [currentCourse]);

	const finalExam = useMemo(() => {
		return currentCourse?.courseChapters?.find(chap => chap.chapterOrderInCourse === currentCourse.courseChapters.length);
	}, [currentCourse]);

	const displayPopupCoursesBeforeFinalExam = useMemo(() => {
		//list of courses we dont want to display the last chapter in the popup.(like IELTS popup before entering final exam)
		if (currentCourse && [COURSE_IDS.TCF].includes(currentCourse.courseId)) return false;
		return isIELTSCourse || isTOFELCourse || isTEFCourse;
	}, [currentCourse, isIELTSCourse, isTOFELCourse, isTEFCourse]);

	const _renderDetails = ({
		courseChapterId,
		chapterName,
		chapterDescription,
		chapterURL,
		testURL,
		testHeaderText,
		courseChapterStatus,
		chapterGroups,
		isFinal,
		classMaterial,
		isOccured,
		allowedToTakeTestIELTS = true,
	}) => {
		const disabled = _checkForDetailsDisable({ isOccured, isFinal });
		const quizDisabled = _checkIfTheQuizIsDisabled(allowedToTakeTestIELTS, isFinal);

		//put message as title for the final exam details component
		const detailsTitle = isFinal ? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_final_exam_message`) : '';

		return (
			<Details
				recordId={recordId}
				key={courseChapterId}
				title={chapterName}
				chapter={{ chapterURL, chapterDescription }}
				displayPopupCoursesBeforeFinalExam={displayPopupCoursesBeforeFinalExam}
				quiz={{ testURL, testHeaderText, isDisabled: quizDisabled }}
				chapterId={courseChapterId}
				progress={courseChapterStatus}
				materials={classMaterial}
				materialGroups={chapterGroups}
				disabled={disabled}
				isOccured={isOccured}
				isTEF={isTEFCourse}
				isFinal={isFinal}
				middlewareCallback={toggleIeltsModalVisible}
				pdfCallBack={_handleViewPdf}
				detailsTitle={detailsTitle}
			/>
		);
	};
	const _renderUnit = ({ title, courseChapters, unitStatus, classMaterial, ...rest }, idx) => {
		return (
			<Details
				isUnit
				recordId={recordId}
				key={idx}
				title={title}
				displayPopupCoursesBeforeFinalExam={displayPopupCoursesBeforeFinalExam}
				courseChapters={courseChapters}
				materials={classMaterial}
				progress={unitStatus}
				isFinal={false}
				renderDetails={_renderDetails}
				pdfCallBack={_handleViewPdf}
			/>
		);
	};

	useEffect(() => {
		const course = courses?.courses?.find(cour => cour.recordId == recordId);
		if (!course) return;

		if (
			(course.courseTypeId === COURSE_TYPES.INTERNAL ||
				course.courseTypeId === COURSE_TYPES.IELTS_GENERAL ||
				course.courseTypeId === COURSE_TYPES.TEF ||
				course.courseTypeId === COURSE_TYPES.TOFEL) &&
			course.studentCourseStatusName === 'New'
		) {
			_handleChangeFirstStatus();
		}
		setCurrentCourse(course);
		dispatchCourses({ type: COURSES_ACTIONS.SET_CURRENT_COURSE, payload: course });
	}, [courses?.courses]);

	//clear certificate states
	useEffect(() => {
		return () => {
			setCertificatePdf();
			setPdfBlob();
		};
	}, []);

	const _handleCertificate = async () => {
		if (!recordId) return;
		//if the user already generate the certificate dont generate again -> exclude IELTS courses that their certificate came only in ENGLISH (business logic)
		if ([COURSE_TYPES.IELTS, COURSE_TYPES.IELTS_GENERAL].includes(currentCourse.courseTypeId) && pdfBlob && certificatePdf) {
			toggleESLModalVisible();
			return;
		}

		const _pdfBlob = await Api.GetCertificate(recordId);
		const _pngBlob = await Api.GetCertificatePng(recordId);
		if (!_pdfBlob || !_pngBlob) return;

		const reader = new FileReader();
		reader.onloadend = function () {
			const base64data = reader.result;
			setCertificatePdf(base64data);
		};

		reader.readAsDataURL(_pngBlob);
		setPdfBlob(_pdfBlob);
		toggleESLModalVisible();
	};

	//handle Navigation to IELTS Final Exam on FinalExam Popup
	const _handleIELTSTestNavigation = () => {
		if (!finalExam) return;
		try {
			const path = generatePath(finalExam.testURL, {
				recordId: finalExam.recordId,
				chapterId: finalExam.courseChapterId,
			});
			navigate(path);
		} catch (error) {
			console.warn(`Enable to navigate to final Exam`);
		}
	};

	//handle show pdf on pop up
	const _handleViewPdf = url => {
		if (!url) return;
		setPdfToDisplay(url);
		togglePdfModalVisible();
	};

	const _handleDownLoadPdf = () => {
		if (!pdfBlob) return;

		const courseName = currentCourse?.courseName || '';
		const link = document.createElement('a');
		link.href = URL.createObjectURL(pdfBlob); //create a blobURI pointing to our Blob
		link.download = `${courseName} Certificate`; // some browser needs the anchor to be in the doc
		link.click();
		setTimeout(() => URL.revokeObjectURL(link.href), 1000); // in case the Blob uses a lot of memory
	};

	//#region prepMyFuture
	// const _handleOnlinePlatformClick = async () => {
	// 	const res = await Api.OnlinePlatformPrepMyFuture(recordId);
	// 	if (res) showResponseMessagePopup('Please check your email for details');
	// 	else showResponseMessagePopup('Please contact our support', true);
	// };
	//#endregion

	//handles first course click on personalArea to change status for both IELTS OR TEF
	const _handleChangeFirstStatus = async () => {
		await Api.UpdateFirstCourseStatus(recordId);
	};

	const _handleOnlinePlatformClick = () => {
		const courseName = currentCourse?.courseName || '';
		fetch('https://materials.galwayacademy.com/CourseID_5/IELTS_Preparation_Course_Syllabus.pdf')
			.then(response => response.blob())
			.then(blob => {
				const link = document.createElement('a');
				link.href = URL.createObjectURL(blob);
				link.download = `${courseName} Certificate`; // some browser needs the anchor to be in the doc
				link.click();
				setTimeout(() => URL.revokeObjectURL(link.href), 1000); // in case the Blob uses a lot of memory
			});
	};

	const _checkForDetailsDisable = ({ isOccured, isFinal }) => {
		//ment for as possiable generic Disable check
		if ((currentCourse.courseTypeId === COURSE_TYPES.IELTS || currentCourse.courseTypeId === COURSE_TYPES.IELTS_GENERAL) && !isOccured)
			return true; //IELTS method to check if chapter should be disabled
		//Restrict final exam chapter in esl if student didn't occur session 8 - only the regular chapters will be always opened.
		else if (currentCourse.courseTypeId === COURSE_TYPES.ESL && isFinal && !isOccured) return true;
		return false;
	};

	const _checkIfTheQuizIsDisabled = (allowedToTakeTestIELTS, isFinal) => {
		//IELTS and TOFEL -> allowedToTakeTestIELTS backend took control.
		if (isIELTSCourse || isTOFELCourse) return !allowedToTakeTestIELTS;
		//TEF -> we dont want to disable the final exam.
		else if (isTEFCourse) return false;
		//Internal -> need to finish all quizez to open the final quiz.
		else return isFinal && !passedAllTests;
	};

	const _handlePressbackButton = () => {
		if ([COURSE_TYPES.ESL, COURSE_TYPES.IELTS].includes(currentCourse.courseTypeId)) {
			navigate(`${ROUTES.MY_COURSE}/${recordId}`);
			return;
		}
		navigate(ROUTES.PERSONAL_AREA);
	};

	//handles redirect to Conversation
	const _handleContactYourTeacher = () => {
		navigate(ROUTES.CONVERSATION, {
			state: { contactTeacher: true, studentTeacherId: currentCourse.studentTeacherId, recordId: currentCourse.recordId },
		});
	};

	return (
		<div className="Course">
			<Container>
				{currentCourse && (
					<>
						<Button
							small
							type="outline"
							startIcon="houseIcon"
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_back_button`)}
							onClick={_handlePressbackButton}
						/>
						<div className="Course__course_title">{currentCourse?.courseName ?? ''}</div>
						{currentCourse.courseTypeId === COURSE_TYPES.IELTS && (
							<div className="Course__course_sub_title"> {getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_IELTS_sub_title`)}</div>
						)}
						<div className="main_wrapper">
							<div className="chapters_wrapper">
								{currentCourse?.units && (
									<div className="units_wrapper">{currentCourse?.units && currentCourse?.units.map(_renderUnit)}</div>
								)}
								{currentCourse?.courseChapters?.sort((a, b) => a.chapterOrderInCourse > b.chapterOrderInCourse).map(_renderDetails)}
							</div>
							<div className="messages_wrapper">
								{(courseCertificateEligible || displayCourseCertificateGeneralIELTS) && (
									<div className="certificate">
										<Icon icon="rightArrow" size={screenSize.width > TABLET_SIZE ? 55 : 25} />
										<CertificateProgress
											progress={currentCourse?.rateOfProgress || 0}
											onClick={_handleCertificate}
											courseType={currentCourse?.courseTypeId}
										/>
										{(currentCourse?.courseId === COURSE_IDS.IELTS && currentCourse?.studentTeacherId && (
											<Button
												title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_IELTS_contact_button`)}
												startIcon="contactIcon"
												iconSize={25}
												extraClassName="contact_teacher"
												onClick={_handleContactYourTeacher}
											/>
										)) ||
											null}
									</div>
								)}
								{/* {isIELTSCourse && (
									<div className="gray_box online_platform">
										<p>In order to view your Syllabus, click here:</p>
										<Button title="Continue" onClick={_handleOnlinePlatformClick} endIcon="rightArrow" small />
									</div>
								)} */}
							</div>
						</div>
					</>
				)}
			</Container>
			<Modal isVisible={eslModalVisible} setVisibility={toggleESLModalVisible}>
				<img className="certificate_pdf__img" src={`${certificatePdf}`} />
				<Button
					title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_ESL_modal_button`)}
					onClick={_handleDownLoadPdf}
					endIcon="downloadIcon"
					small
				/>
			</Modal>
			<Modal isVisible={ieltsModalVisible} setVisibility={toggleIeltsModalVisible}>
				<div className="TestModal" dangerouslySetInnerHTML={{ __html: finalExam?.chapterBodyText }} />
				<Button title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_IELTS_modal_button`)} onClick={_handleIELTSTestNavigation} />
			</Modal>
			<Modal isVisible={pdfModalVisible} setVisibility={togglePdfModalVisible} extraClassName="pdf_modal">
				<object data={pdfToDisplay + '#view=FitH'} type="application/pdf" width="100%" height="100%">
					<UnsupportedFile linkToDisplay={pdfToDisplay} />
				</object>
			</Modal>
		</div>
	);
};

export default Course;
